import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Heart, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { StarField } from "../components/StarField";
import { ZodiacWheel } from "../components/ZodiacWheel";
import { WesternAspectList, ChineseRelationList } from "../components/AspectPanel";
import { ExportButtons } from "../components/ExportButtons";

function renderMarkdown(text) {
  if (!text) return null;
  return text.split("\n").filter((l) => l.trim() && !/^-{3,}$/.test(l.trim())).map((line, i) => {
    if (line.startsWith("### ")) return <h3 key={i} className="text-xl font-serif gold-text mt-4 mb-1">{line.slice(4)}</h3>;
    if (line.startsWith("## ")) return <h2 key={i}>{line.slice(3)}</h2>;
    if (line.startsWith("# ")) return <h2 key={i}>{line.slice(2)}</h2>;
    const html = line.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    return <p key={i} dangerouslySetInnerHTML={{ __html: html }} />;
  });
}

export default function SynastryPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [charts, setCharts] = useState([]);
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [synError, setSynError] = useState(null);

  useEffect(() => {
    api.get("/charts").then((r) => setCharts(r.data.charts));
  }, []);

  useEffect(() => {
    const qid = params.get("id");
    const qa = params.get("a"), qb = params.get("b");
    if (qid) {
      loadById(qid);
    } else if (qa && qb) {
      setA(qa); setB(qb);
      runPair(qa, qb);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadById = async (synId) => {
    setLoading(true);
    setResult(null);
    setSynError(null);
    try {
      const { data } = await api.get(`/synastry/${synId}`);
      setResult(data);
      setLoading(false);
      if (data.status === "error") setSynError(data.error || "Generation failed");
      else if (data.status !== "ready") pollSynastry(synId, 0);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  const runPair = async (ca, cb) => {
    setLoading(true);
    setResult(null);
    setSynError(null);
    try {
      const { data } = await api.post("/synastry", { chart_a: ca, chart_b: cb });
      setResult(data);
      setLoading(false);
      if (data.status === "error") setSynError(data.error || "Generation failed");
      else if (data.status !== "ready") pollSynastry(data.id, 0);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  const run = () => {
    if (!a || !b) return toast.error("Pick two charts");
    if (a === b) return toast.error("Choose two different people");
    runPair(a, b);
  };

  const pollSynastry = (synId, attempt) => {
    const tick = async () => {
      try {
        const { data } = await api.get(`/synastry/${synId}`);
        if (data.status === "ready") { setResult(data); return; }
        if (data.status === "error") { setSynError(data.error || "Generation failed"); return; }
      } catch { /* keep polling */ }
      if (attempt >= 45) { setSynError("This is taking longer than expected. Please try again."); return; }
      setTimeout(() => pollSynastry(synId, attempt + 1), 4000);
    };
    setTimeout(tick, 4000);
  };

  const selectCls = "w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-sm outline-none focus:border-[#45F0DF]/50 [color-scheme:dark]";

  return (
    <div className="min-h-screen cosmic-bg grain relative pb-24">
      <StarField count={60} />
      <header className="glass sticky top-0 z-40 border-b border-white/10">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate("/")} data-testid="syn-back-btn" className="text-white/60 hover:text-white transition-colors"><ArrowLeft className="h-5 w-5" /></button>
          <div className="flex items-center gap-2">
            <Heart className="h-5 w-5 gold-text" strokeWidth={1.5} />
            <h1 className="text-2xl font-serif leading-none">Synastry</h1>
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-6xl px-6 py-10">
        <p className="overline gold-text mb-3">Relationship Compatibility</p>
        <h2 className="text-5xl font-serif tracking-tight leading-none mb-10">Two Charts, One Sky</h2>

        {charts.length < 2 ? (
          <div className="glass rounded-2xl p-10 text-center">
            <p className="text-white/60">You need at least two saved charts to run a synastry reading.</p>
            <button onClick={() => navigate("/")} className="btn-gold mt-5 rounded-full px-6 py-3 text-sm font-semibold">Create Charts</button>
          </div>
        ) : (
          <div className="glass rounded-2xl p-6 grid sm:grid-cols-[1fr_1fr_auto] gap-4 items-end mb-10">
            <div>
              <label className="overline text-white/40 block mb-2">Person A</label>
              <select data-testid="synastry-select-a" value={a} onChange={(e) => setA(e.target.value)} className={selectCls}>
                <option value="">Select chart…</option>
                {charts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="overline text-white/40 block mb-2">Person B</label>
              <select data-testid="synastry-select-b" value={b} onChange={(e) => setB(e.target.value)} className={selectCls}>
                <option value="">Select chart…</option>
                {charts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <button onClick={run} disabled={loading} data-testid="synastry-generate" className="btn-gold rounded-lg px-6 py-3 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Reveal
            </button>
          </div>
        )}

        {loading && (
          <div className="flex flex-col items-center py-16 text-center">
            <Loader2 className="h-8 w-8 animate-spin gold-text mb-4" />
            <p className="text-white/50 text-sm">Weaving two charts together…</p>
          </div>
        )}

        {result && (
          <div className="space-y-8" data-testid="synastry-result">
            <div className="grid gap-6 lg:grid-cols-2">
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-6">
                <p className="overline gold-text mb-3">Bi-Wheel · {result.a_name} (inner) &amp; {result.b_name} (outer)</p>
                <ZodiacWheel
                  planets={result.a_charts.hellenistic.planets}
                  outerPlanets={result.b_charts.hellenistic.planets}
                  ascendant={result.a_charts.hellenistic.ascendant}
                  outerAscendant={result.b_charts.hellenistic.ascendant}
                  accent="#45F0DF" outerAccent="#D4AF37"
                  aspects={result.data.western}
                  showHouses
                  size={380}
                />
                <p className="text-[10px] text-white/40 mt-2 text-center">House numbers shown as {result.a_name}·{result.b_name} per sign</p>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-6 space-y-5">
                <div>
                  <p className="overline gold-text mb-2">Key Cross-Aspects</p>
                  <WesternAspectList aspects={result.data.western.slice(0, 12)} />
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="rounded-lg bg-white/5 p-3">
                    <span className="text-white/40">Chinese Animals</span><br />
                    {result.data.chinese.a_animal} &amp; {result.data.chinese.b_animal}
                  </div>
                  <div className="rounded-lg bg-white/5 p-3">
                    <span className="text-white/40">Vedic Moon</span><br />
                    {result.data.vedic.a_moon} ↔ {result.data.vedic.b_moon} ({result.data.vedic.harmony})
                  </div>
                </div>
                <div>
                  <p className="overline mb-2" style={{ color: "#B47CF0" }}>Chinese Pillar Relations</p>
                  <ChineseRelationList relations={{ branch_relations: result.data.chinese.relations }} />
                </div>
              </motion.div>
            </div>

            {/* Vedic Ashtakoota + BaZi interaction tables (per-system convention) */}
            <div className="grid gap-6 lg:grid-cols-2">
              {result.data.vedic.ashtakoota && (
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-6" data-testid="ashtakoota-table">
                  <div className="flex items-center justify-between mb-3">
                    <p className="overline cyan-text">Vedic · Ashtakoota (Guna Milan)</p>
                    <span className="text-sm gold-text">{result.data.vedic.ashtakoota.total} / 36 · {result.data.vedic.ashtakoota.verdict}</span>
                  </div>
                  <table className="w-full text-sm">
                    <tbody>
                      {Object.entries(result.data.vedic.ashtakoota.components).map(([k, v]) => (
                        <tr key={k} className="border-b border-white/5">
                          <td className="py-1.5 text-white/70">{k}</td>
                          <td className="py-1.5 text-right">
                            <span style={{ color: v.score >= v.max * 0.6 ? "#7FD4A0" : v.score === 0 ? "#F26D6D" : "rgba(255,255,255,0.6)" }}>{v.score}</span>
                            <span className="text-white/30"> / {v.max}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </motion.div>
              )}

              {result.data.chinese.cross && (
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-6" data-testid="bazi-cross-table">
                  <p className="overline mb-3" style={{ color: "#B47CF0" }}>BaZi · Cross Interactions</p>
                  <div className="rounded-lg bg-white/5 p-3 mb-3">
                    <span className="text-xs text-white/40">Spouse Palace (Day Branches)</span>
                    <div className="text-sm">{result.data.chinese.cross.spouse_palace.type}</div>
                    <div className="text-xs text-white/50">{result.data.chinese.cross.spouse_palace.note}</div>
                  </div>
                  {result.data.chinese.cross.stem_combinations.length > 0 && (
                    <div className="mb-2">
                      <p className="text-xs text-white/40 mb-1">Stem Combinations</p>
                      {result.data.chinese.cross.stem_combinations.map((s, i) => (
                        <div key={i} className="text-xs text-white/70">{s.between.join(" + ")} → {s.transforms_to}</div>
                      ))}
                    </div>
                  )}
                  <ChineseRelationList relations={{ branch_relations: result.data.chinese.cross.branch_relations }} />
                </motion.div>
              )}
            </div>

            <div className="glass rounded-2xl p-8 sm:p-10">
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <p className="overline gold-text mb-2">The Reading</p>
                  <h3 className="text-4xl font-serif tracking-tight">{result.a_name} &amp; {result.b_name}</h3>
                </div>
                <ExportButtons base={`/synastry/${result.id}`} name={`AstroTrinity_Synastry`} />
              </div>
              {result.report ? (
                <div className="report-content" data-testid="synastry-report-content">{renderMarkdown(result.report)}</div>
              ) : synError ? (
                <div className="flex flex-col items-center py-12 text-center" data-testid="synastry-error">
                  <p className="text-white/70 mb-1">The oracle couldn't complete this reading.</p>
                  <p className="text-white/40 text-xs mb-5 max-w-md">{/budget/i.test(synError) ? "The AI credits for this app have run out. Please top up the Universal Key balance, then try again." : synError}</p>
                  <button onClick={() => runPair(result.chart_a, result.chart_b)} data-testid="synastry-retry" className="btn-gold rounded-full px-5 py-2.5 text-sm font-semibold">Try Again</button>
                </div>
              ) : (
                <div className="flex flex-col items-center py-12 text-center">
                  <Loader2 className="h-7 w-7 animate-spin gold-text mb-3" />
                  <p className="text-white/50 text-sm">Weaving your compatibility reading… this can take up to a minute.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
